import type { CustomCellEditorProps } from "ag-grid-react";
import { type FC } from "react";
import { type FileDefinitionType } from "widgets/utils";

import { FilePickerControlledDialog } from "@/modules/files/components/FilePickerControlledDialog.tsx";
import { isFileOfType, sortProjectFileByFileName } from "@/modules/files/utils/file.ts";

import { type FileType, type ProjectFile } from "../../../../../graphql.ts";
import { ATTRIBUTE_FILE_TYPES } from "../../../../../settings/settings.ts";
import { type IAgGridCellProps } from "../../cell-base-interfaces.ts";
import { type CellEditorParams } from "../../cell-editor-factory.tsx";
import FileCell from "../core/FileCell.tsx";
import { TableCell } from "../core/TableCell.tsx";

export const EditableFileCell: FC<IAgGridCellProps> = (props) => {
  return (
    <FileCell
      {...props}
      triggerButtonProps={
        props.node.rowIndex != undefined && props.column != undefined
          ? {
              label: "Browse Files",
              icon: "folder_open",
              onPress: () => props.api.startEditingCell({ rowIndex: props.node.rowIndex!, colKey: props.column! }),
            }
          : undefined
      }
    />
  );
};

export const FileCellEditor = (props: CustomCellEditorProps & CellEditorParams) => {
  const { fileType } = props;
  const fileFilterTypes = getFilePickerFilterExtensions(fileType);

  return (
    <TableCell>
      <FilePickerControlledDialog
        isSelectableFilter={
          fileType?.id === ATTRIBUTE_FILE_TYPES.ANY
            ? () => true
            : (file) => {
                const filename = file.fileName ?? file.fullPath;
                return isFileOfType(fileFilterTypes)({ ...file, fileName: filename });
              }
        }
        baseFilterHint={
          fileFilterTypes
            ? `Files of type ${fileFilterTypes.join(", ")} can be selected.`
            : "Any file or folder can be selected"
        }
        fileDefinitionTypes={fileFilterTypes}
        onValid={(files) => {
          const sortedFiles = sortProjectFileByFileName(files as ProjectFile[]);
          const columnField = props.column?.getColId();
          const done = props.node.setDataValue(columnField, sortedFiles);

          return done ? Promise.resolve() : Promise.reject();
        }}
        isOpen
        // This is non-sense to cancel the editing but it seems to block the reset that comes somehow just after editing.
        // The DataTable is so crazy I couldn't find the race condition :-/
        onClose={() => props.api.stopEditing(true)}
      />
    </TableCell>
  );
};

function getFilePickerFilterExtensions(fileType: FileType): FileDefinitionType[] | null {
  const attributeFileType = fileType?.id;

  if (attributeFileType === ATTRIBUTE_FILE_TYPES.UNALIGNED_READ_DATA) {
    return ["FASTQ"];
  } else if (attributeFileType === ATTRIBUTE_FILE_TYPES.ALIGNED_READ_DATA) {
    return ["BAM", "SAM"];
  }

  return null;
}
